/* ============================================================================
   MALSERA — ms-product.js
   Galería, selector de variante, botón de compra, barra ATC fija y tarjetas
   de "Combínalo con". Componentes web nativos, sin framework.
   ============================================================================ */

/** Ancho a partir del cual la miniatura pasa de 64 a 88. El mismo 1024 que
    usa el CSS para cambiar la ficha a dos columnas. */
const MS_ESCRITORIO = '(min-width: 1024px)';

/**
 * Galería. Dos Splide sincronizados: la foto grande y la tira de miniaturas.
 *
 * El indicador del canto inferior es la paginación de Splide, solo que
 * recompuesta por CSS. No se construye una propia: el teclado, el aria-current
 * y el anuncio al lector de pantalla vienen dados.
 */
class MsGallery extends HTMLElement {
  connectedCallback() {
    this.principal = this.querySelector('[data-ms-gallery-main]');
    this.miniaturas = this.querySelector('[data-ms-gallery-thumbs]');
    if (!this.principal) return;

    if (typeof Splide === 'undefined') {
      document.addEventListener('ms:splide-listo', () => this.montar(), { once: true });
    } else {
      this.montar();
    }
  }

  montar() {
    this.splidePrincipal = new Splide(this.principal, {
      type: 'slide',
      arrows: false,
      pagination: true,
      // Arrastre también en escritorio (10-10-2026). Antes allí estaba
      // apagado porque se navegaba con las miniaturas; sin ellas, el único
      // control sería acertar una raya de 1px. La imagen entera es un
      // objetivo mejor que cualquier mando que pudiéramos añadir encima.
      drag: true,
      speed: 400,
      classes: { pagination: 'splide__pagination', page: 'splide__pagination__page' },
      // Splide trae sus etiquetas en inglés y solo estaba traducida una, así
      // que la regleta anunciaba «Go to slide 1» en una tienda española
      // (10-10-2026). Se traducen todas las que este carrusel usa.
      i18n: {
        carousel: 'Imágenes del producto',
        slide: 'imagen',
        select: 'Elige una imagen',
        slideX: 'Ir a la imagen %s',
        slideLabel: 'Imagen %s de %s',
        prev: 'Imagen anterior',
        next: 'Imagen siguiente',
        first: 'Ir a la primera imagen',
        last: 'Ir a la última imagen',
      },
    });

    // La tira. `isNavigation` es lo que la convierte en un mando de verdad:
    // pone el rol, el aria-selected y el teclado en cada miniatura. El sync
    // tiene que ir ANTES de los mount, o cada carrusel va por su cuenta.
    if (this.miniaturas) {
      const enEscritorio = window.matchMedia(MS_ESCRITORIO).matches;
      this.splideMiniaturas = new Splide(this.miniaturas, {
        fixedWidth: enEscritorio ? 88 : 64,
        fixedHeight: enEscritorio ? 88 : 64,
        gap: 8,
        rewind: true,
        pagination: false,
        arrows: false,
        isNavigation: true,
        drag: 'free',
        focus: 0,
        i18n: { carousel: 'Miniaturas', slide: 'miniatura', slideX: 'Ir a la imagen %s' },
      });

      this.splidePrincipal.sync(this.splideMiniaturas);
      this.splidePrincipal.mount();
      this.splideMiniaturas.mount();
      return;
    }

    this.splidePrincipal.mount();
  }

  /** Salta a la imagen de una variante. La llama el selector de color. */
  irAMedia(idMedia) {
    if (!this.splidePrincipal || !idMedia) return;
    const diapositivas = [...this.querySelectorAll('[data-ms-gallery-main] .splide__slide')];
    const indice = diapositivas.findIndex((d) => d.dataset.mediaId === String(idMedia));
    if (indice >= 0) this.splidePrincipal.go(indice);
  }

  disconnectedCallback() {
    this.splidePrincipal?.destroy();
    this.splideMiniaturas?.destroy();
  }
}
customElements.define('ms-gallery', MsGallery);

/**
 * Ficha completa: sincroniza selector, galería, precio, botón y barra fija.
 * Ningún cambio de variante recarga la página ni salta de altura.
 */
class MsProduct extends HTMLElement {
  connectedCallback() {
    this.variantes = this.leerVariantes();
    if (!this.variantes.length) return;

    this.galeria = this.querySelector('ms-gallery');
    this.select = this.querySelector('[data-ms-select]');
    this.lista = this.querySelector('[data-ms-select-list]');
    this.formulario = this.querySelector('[data-ms-form]');
    this.campoId = this.querySelector('[data-ms-variante-id]');
    this.boton = this.querySelector('[data-ms-boton]');
    this.textoBoton = this.querySelector('[data-ms-boton-texto]');
    this.precio = this.querySelector('[data-ms-price]');
    this.comparativo = this.querySelector('[data-ms-price-compare]');
    this.ahorro = this.querySelector('[data-ms-ahorro]');
    this.ahorroImporte = this.querySelector('[data-ms-ahorro-importe]');
    this.error = this.querySelector('[data-ms-error]');

    this.barra = document.querySelector('[data-ms-sticky-atc]');
    this.botonBarra = this.barra?.querySelector('[data-ms-sticky-boton]');
    this.textoBarra = this.barra?.querySelector('[data-ms-sticky-texto]');
    this.precioBarra = this.barra?.querySelector('[data-ms-barra-precio]');
    this.pastillaBarra = this.barra?.querySelector('[data-ms-barra-toggle]');
    this.listaBarra = this.barra?.querySelector('[data-ms-barra-lista]');
    this.dotBarra = this.barra?.querySelector('[data-ms-barra-dot]');
    this.nombreBarra = this.barra?.querySelector('[data-ms-barra-nombre]');
    this.cantBarra = this.barra?.querySelector('[data-ms-barra-cant-valor]');
    this.thumbBarra = this.barra?.querySelector('[data-ms-barra-thumb]');
    this.cantidad = 1;

    this.actual = this.variantes.find((v) => String(v.id) === this.campoId?.value) || this.variantes[0];

    this.conectarDesplegable();
    this.conectarMuestras();
    this.conectarFormulario();
    this.conectarBarraFija();
  }

  leerVariantes() {
    const nodo = this.querySelector('[data-ms-variantes]');
    if (!nodo) return [];
    try {
      return JSON.parse(nodo.textContent);
    } catch (e) {
      return [];
    }
  }

  /* --- Capa 1: desplegable ------------------------------------------------ */

  conectarDesplegable() {
    if (!this.select) return;
    const disparador = this.select.querySelector('[data-ms-select-toggle]');

    disparador?.addEventListener('click', () => {
      this.select.classList.contains('is-open') ? this.cerrarLista() : this.abrirLista();
    });

    this.lista?.querySelectorAll('[data-ms-option-valor]').forEach((opcion) => {
      opcion.addEventListener('click', () => {
        this.seleccionar(opcion.dataset.msOptionValor);
        this.cerrarLista();
        disparador?.focus();
      });
    });

    this.alPulsarFuera = (evento) => {
      if (!this.select.contains(evento.target)) this.cerrarLista();
    };
    this.alPulsarTecla = (evento) => {
      if (evento.key === 'Escape') {
        this.cerrarLista();
        disparador?.focus();
      }
    };
  }

  abrirLista() {
    this.select.classList.add('is-open');
    this.lista.hidden = false;
    this.select.querySelector('[data-ms-select-toggle]')?.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.alPulsarFuera);
    document.addEventListener('keydown', this.alPulsarTecla);
  }

  cerrarLista() {
    if (!this.select?.classList.contains('is-open')) return;
    this.select.classList.remove('is-open');
    this.lista.hidden = true;
    this.select.querySelector('[data-ms-select-toggle]')?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.alPulsarFuera);
    document.removeEventListener('keydown', this.alPulsarTecla);
  }

  /* --- Capa 2: muestras --------------------------------------------------- */

  conectarMuestras() {
    this.muestras = [...this.querySelectorAll('[data-ms-swatches] .ms-swatch')];

    this.muestras.forEach((muestra, indice) => {
      muestra.addEventListener('click', () => this.seleccionar(muestra.dataset.msOptionValor));

      // Navegable con flechas, como pide un radiogroup.
      muestra.addEventListener('keydown', (evento) => {
        const salto = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[evento.key];
        if (!salto) return;
        evento.preventDefault();
        const siguiente = this.muestras[(indice + salto + this.muestras.length) % this.muestras.length];
        siguiente.focus();
        this.seleccionar(siguiente.dataset.msOptionValor);
      });
    });
  }

  /* --- Cambio de variante -------------------------------------------------- */

  seleccionar(valor) {
    const variante = this.variantes.find((v) => v.valor === valor);
    if (!variante || variante.id === this.actual.id) return;
    this.actual = variante;

    // Desplegable
    const nombre = this.querySelector('[data-ms-select-name]');
    const subtitulo = this.querySelector('[data-ms-select-sub]');
    const muestraSel = this.querySelector('[data-ms-select-swatch]');
    if (nombre) nombre.textContent = variante.titulo;
    if (subtitulo) subtitulo.textContent = variante.subtitulo || '';
    if (muestraSel) this.pintarMuestra(muestraSel, variante);

    this.lista?.querySelectorAll('[data-ms-option-valor]').forEach((opcion) => {
      opcion.setAttribute('aria-selected', opcion.dataset.msOptionValor === valor ? 'true' : 'false');
    });

    // Muestras
    this.muestras?.forEach((muestra) => {
      muestra.setAttribute('aria-checked', muestra.dataset.msOptionValor === valor ? 'true' : 'false');
    });

    // Precio, descuento y etiqueta de ahorro
    if (this.precio) this.precio.textContent = variante.precio;

    if (this.comparativo) {
      this.comparativo.textContent = variante.comparativo || '';
      this.comparativo.hidden = !variante.comparativo;
    }

    // El descuento va al lado de los precios y en porcentaje. Sin rebaja no
    // se pinta: un «−0 %» es peor que nada.
    if (this.ahorro) {
      if (this.ahorroImporte) this.ahorroImporte.textContent = variante.ahorro_pct || '';
      this.ahorro.hidden = !variante.ahorro_pct;
    }

    // Botón y barra fija
    if (this.campoId) this.campoId.value = variante.id;
    // Misma etiqueta en los dos, y sin precio: el de la ficha lo tiene encima
    // y la barra fija lleva el nombre del bolso al lado.
    this.pintarBoton(this.boton, this.textoBoton, variante, 'Añadir al carrito');
    this.pintarBoton(this.botonBarra, this.textoBarra, variante, 'Añadir al carrito');

    // La barra fija: precio dentro del botón, muestra y nombre en la píldora,
    // y la miniatura del color elegido. Si la variante no trae imagen propia
    // se deja la que haya puesta: mejor la del producto que un hueco.
    this.pintarPrecioBarra();
    if (this.thumbBarra && variante.thumb) this.thumbBarra.src = variante.thumb;
    if (this.nombreBarra) this.nombreBarra.textContent = variante.valor;
    if (this.dotBarra) this.pintarMuestra(this.dotBarra, variante);
    this.listaBarra?.querySelectorAll('[data-ms-barra-valor]').forEach((opcion) => {
      opcion.setAttribute('aria-selected', opcion.dataset.msBarraValor === valor ? 'true' : 'false');
    });

    // Galería
    this.galeria?.irAMedia(variante.media);

    // URL, sin recargar
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variante.id);
    window.history.replaceState({}, '', url);
  }

  /** Color plano o, en los estampados, el teselado del material. Nunca una
      foto del bolso. Sin ninguno de los dos no se pinta círculo: uno vacío
      parece un fallo de carga.

      El color va siempre, también bajo la imagen: es el respaldo si el
      archivo no llega. La imagen sí se limpia al pasar a un color plano; si
      no, se quedaría debajo para siempre. */
  pintarMuestra(elemento, variante) {
    const recorte = variante.swatch_img;
    elemento.hidden = !recorte && !variante.swatch;
    elemento.style.backgroundColor = variante.swatch || '';
    elemento.style.backgroundImage = recorte ? `url(${recorte})` : '';
  }

  /** El botón nunca cambia de altura al cambiar de estado. */
  pintarBoton(boton, texto, variante, etiqueta) {
    if (!boton || !texto) return;
    boton.disabled = !variante.disponible;
    texto.textContent = variante.disponible ? etiqueta : 'Agotado';
  }

  /* --- Añadir al carrito --------------------------------------------------- */

  conectarFormulario() {
    this.formulario?.addEventListener('submit', (evento) => {
      evento.preventDefault();
      this.anadir(this.boton);
    });

    this.botonBarra?.addEventListener('click', () => this.anadir(this.botonBarra));
  }

  async anadir(boton) {
    if (!boton || boton.disabled) return;
    if (this.error) this.error.textContent = '';
    boton.classList.add('is-loading');
    boton.disabled = true;

    try {
      const respuesta = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: this.actual.id, quantity: this.cantidad }] }),
      });

      if (!respuesta.ok) {
        const datos = await respuesta.json().catch(() => ({}));
        throw new Error(datos.description || 'No hemos podido añadir el bolso.');
      }

      // La cantidad vuelve a 1 después de un añadido correcto. La barra sigue
      // en pantalla, y dejar un 3 ahí invita a añadir de más sin querer.
      this.ponerCantidad(1);
      document.dispatchEvent(new CustomEvent('ms:carrito-cambiado', { detail: { abrir: true } }));
    } catch (fallo) {
      if (this.error) this.error.textContent = fallo.message;
    } finally {
      boton.classList.remove('is-loading');
      boton.disabled = !this.actual.disponible;
    }
  }

  /* --- Barra ATC fija ------------------------------------------------------ */

  /** Cantidad, entre 1 y 10. Diez bolsos iguales no es una compra, es un
      error de dedo, y un tope evita que el stepper se vuelva infinito. */
  ponerCantidad(n) {
    this.cantidad = Math.min(10, Math.max(1, n));
    if (this.cantBarra) this.cantBarra.textContent = String(this.cantidad);
    this.barra?.querySelectorAll('[data-ms-barra-cant]').forEach((boton) => {
      const paso = Number(boton.dataset.msBarraCant);
      boton.disabled = paso < 0 ? this.cantidad <= 1 : this.cantidad >= 10;
    });
    this.pintarPrecioBarra();
  }

  /** El precio del botón es el TOTAL: precio de la variante por la cantidad.
      Si pone 2 y el botón sigue diciendo 74,99, el botón miente sobre lo que
      va a costar el añadido. */
  pintarPrecioBarra() {
    if (!this.precioBarra || !this.actual) return;
    this.precioBarra.textContent = this.multiplicarPrecio(this.actual.precio, this.cantidad);
  }

  /** Multiplica un precio YA FORMATEADO por la tienda, conservando su
      formato. No se reconstruye desde los céntimos: el formato de moneda lo
      decide Shopify en los ajustes (símbolo delante o detrás, coma o punto
      decimal) y replicarlo aquí sería inventarse uno segundo que un día deja
      de coincidir con el del carrito.

      Así que se lee el número de la propia cadena, se multiplica y se vuelve
      a escribir entre el mismo prefijo y el mismo sufijo. Si algo no encaja,
      se devuelve la cadena original sin tocar: antes un precio sin multiplicar
      que un precio inventado. */
  multiplicarPrecio(formateado, n) {
    if (!formateado || n <= 1) return formateado;

    const trozo = formateado.match(/[\d.,]+/);
    if (!trozo) return formateado;

    const crudo = trozo[0];
    const prefijo = formateado.slice(0, trozo.index);
    const sufijo = formateado.slice(trozo.index + crudo.length);

    // Los separadores se LEEN del propio precio, no se deciden aquí.
    // Decimales: un separador seguido de exactamente dos dígitos al final.
    const decimales = /[.,](\d{2})$/.exec(crudo);
    const sepDec = decimales ? crudo[crudo.length - 3] : null;
    const enteroTexto = decimales ? crudo.slice(0, -3) : crudo;
    const milEnOrigen = enteroTexto.match(/[.,]/);
    const sepMil = milEnOrigen ? milEnOrigen[0] : sepDec === ',' ? '.' : ',';

    const entero = Number(enteroTexto.replace(/[.,]/g, ''));
    if (!Number.isFinite(entero)) return formateado;

    // La cuenta va en céntimos: multiplicar decimales en coma flotante
    // acaba dando 149.97999999999999 y un precio con cola no se enseña.
    const centimos = (entero * 100 + (decimales ? Number(decimales[1]) : 0)) * n;
    const parteEntera = String(Math.floor(centimos / 100));
    const parteDecimal = String(centimos % 100).padStart(2, '0');

    const conMiles = parteEntera.replace(/\B(?=(\d{3})+(?!\d))/g, sepMil);
    return prefijo + conMiles + (decimales ? sepDec + parteDecimal : '') + sufijo;
  }
  /** La lista de colores de la barra. Abre hacia arriba, la cierra Escape,
      un clic fuera o elegir un color. Mismo contrato que el desplegable de
      la ficha; no se reimplementa el cierre en dos sitios distintos. */
  conectarColorBarra() {
    if (!this.pastillaBarra || !this.listaBarra) return;

    const cerrar = () => {
      this.listaBarra.hidden = true;
      this.pastillaBarra.setAttribute('aria-expanded', 'false');
    };

    this.pastillaBarra.addEventListener('click', () => {
      const abierta = this.pastillaBarra.getAttribute('aria-expanded') === 'true';
      this.listaBarra.hidden = abierta;
      this.pastillaBarra.setAttribute('aria-expanded', abierta ? 'false' : 'true');
    });

    this.listaBarra.querySelectorAll('[data-ms-barra-valor]').forEach((opcion) => {
      opcion.addEventListener('click', () => {
        this.seleccionar(opcion.dataset.msBarraValor);
        cerrar();
        this.pastillaBarra.focus();
      });
    });

    document.addEventListener('click', (evento) => {
      if (!this.barra.contains(evento.target)) cerrar();
    });

    this.barra.addEventListener('keydown', (evento) => {
      if (evento.key === 'Escape' && !this.listaBarra.hidden) {
        cerrar();
        this.pastillaBarra.focus();
      }
    });
  }

  conectarBarraFija() {
    if (!this.barra || !this.boton) return;

    this.barra.querySelectorAll('[data-ms-barra-cant]').forEach((boton) => {
      boton.addEventListener('click', () => {
        this.ponerCantidad(this.cantidad + Number(boton.dataset.msBarraCant));
      });
    });
    this.ponerCantidad(1);
    this.conectarColorBarra();

    // SOLO HACIA ABAJO. Antes bastaba con que el botón no estuviera a la
    // vista, y eso incluye el tramo de ARRIBA: con la ficha en lo alto, antes
    // de llegar al botón, la barra ya estaba puesta tapando la foto del bolso
    // sin que nadie hubiera visto todavía el producto.
    //
    // La condición se mide sobre el rectángulo real del botón en el momento
    // de decidir, no sobre el que traía el evento: `bottom <= 0` quiere decir
    // que el botón entero ha quedado por encima del viewport.
    const actualizar = () => {
      const visible = this.boton.getBoundingClientRect().bottom <= 0;
      this.barra.classList.toggle('is-visible', visible);
      this.barra.setAttribute('aria-hidden', visible ? 'false' : 'true');
    };

    // Observador dedicado, no listener de scroll: es quien dispara en el uso
    // normal, cuando el botón cruza el borde del viewport.
    this.observador = new IntersectionObserver(actualizar, { threshold: 0 });
    this.observador.observe(this.boton);

    // RED DE SEGURIDAD PARA LOS SALTOS (09-10-2026). Un IntersectionObserver
    // solo avisa cuando se CRUZA el umbral. Si el scroll salta de golpe más
    // de una pantalla y se pasa el botón de largo -un «volver arriba», un
    // ancla, un dedo rápido en el móvil- el botón pasa de estar arriba a estar
    // abajo sin tocar el viewport, no hay cruce, no hay evento, y la barra se
    // quedaba puesta con el producto a la vista. Medido el 09-10-2026.
    //
    // `scrollend` no es un listener de scroll de los que prohíbe el PRD: no
    // corre en cada fotograma, salta una sola vez cuando el scroll se para.
    // Donde el navegador no lo tenga, queda el observador y el único síntoma
    // es ese caso de borde, que es el que había antes.
    this.alParar = actualizar;
    if ('onscrollend' in window) {
      window.addEventListener('scrollend', this.alParar, { passive: true });
    }

    actualizar();
  }

  disconnectedCallback() {
    this.observador?.disconnect();
    if (this.alParar) window.removeEventListener('scrollend', this.alParar);
  }
}
customElements.define('ms-product', MsProduct);

/**
 * Tarjeta de "Combínalo con". Añade sin salir de la ficha y abre el drawer.
 */
class MsPairCard extends HTMLElement {
  connectedCallback() {
    this.formulario = this.querySelector('[data-ms-pair-form]');
    this.campoId = this.querySelector('[data-ms-pair-id]');
    this.texto = this.querySelector('[data-ms-pair-boton]');
    this.nombre = this.querySelector('[data-ms-pair-nombre]');
    this.boton = this.querySelector('button[type="submit"]');

    this.querySelectorAll('[data-ms-pair-valor]').forEach((muestra) => {
      muestra.addEventListener('click', () => {
        this.querySelectorAll('[data-ms-pair-valor]').forEach((otra) => {
          otra.setAttribute('aria-checked', otra === muestra ? 'true' : 'false');
        });
        if (this.campoId) this.campoId.value = muestra.dataset.msVarianteId;
        if (this.texto) this.texto.textContent = `Añadir — ${muestra.dataset.msPrecio}`;
        if (this.nombre) this.nombre.textContent = muestra.dataset.msPairValor;
      });
    });

    // Más de 6 variantes: se muestran 6 y un chevrón que despliega el resto.
    const mas = this.querySelector('[data-ms-pair-more]');
    mas?.addEventListener('click', () => {
      const abierto = mas.getAttribute('aria-expanded') === 'true';
      this.querySelectorAll('[data-ms-pair-extra]').forEach((extra) => {
        extra.hidden = abierto;
      });
      mas.setAttribute('aria-expanded', abierto ? 'false' : 'true');
    });

    this.formulario?.addEventListener('submit', async (evento) => {
      evento.preventDefault();
      if (this.boton.disabled) return;

      this.boton.classList.add('is-loading');
      this.boton.disabled = true;

      try {
        await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(this.campoId.value), quantity: 1 }] }),
        });
        document.dispatchEvent(new CustomEvent('ms:carrito-cambiado', { detail: { abrir: true } }));
      } finally {
        this.boton.classList.remove('is-loading');
        this.boton.disabled = false;
      }
    });
  }
}
customElements.define('ms-pair-card', MsPairCard);

/* Los paneles (<dialog>) viven en ms-panel.js desde el 07-10-2026. Se
   movieron ahí porque el carrito también los usa, y el carrito está en todas
   las páginas mientras que este archivo solo lo carga la ficha. Un panel que
   solo funcionase en la ficha es un botón muerto en el resto de la web. */

/* --- Copiar el código de descuento ----------------------------------------
   El botón copia al portapapeles y lo dice; a los dos segundos vuelve a su
   texto. Si el navegador no deja copiar (sin permiso, o sin HTTPS en una
   vista previa), el código se selecciona para que se pueda copiar a mano:
   quedarse sin respuesta es peor que copiarlo uno mismo.
   ------------------------------------------------------------------------- */
document.addEventListener('click', async (evento) => {
  const boton = evento.target.closest('[data-ms-promo-copiar]');
  if (!boton) return;

  const caja = boton.closest('[data-ms-promo]');
  const codigo = caja?.querySelector('[data-ms-promo-codigo]')?.textContent.trim();
  if (!codigo) return;

  const original = boton.dataset.original || boton.textContent.trim();
  boton.dataset.original = original;

  try {
    await navigator.clipboard.writeText(codigo);
    boton.textContent = boton.dataset.copiado || '¡Copiado!';
  } catch (fallo) {
    const rango = document.createRange();
    rango.selectNodeContents(caja.querySelector('[data-ms-promo-codigo]'));
    const seleccion = window.getSelection();
    seleccion.removeAllRanges();
    seleccion.addRange(rango);
    boton.textContent = 'Copia el código';
  }

  clearTimeout(boton.temporizador);
  boton.temporizador = setTimeout(() => {
    boton.textContent = original;
  }, 2000);
});
